<script lang="ts" setup>
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import { useVeelgesteldeVragenGet } from '@services/crm-public/orval-api/veelgestelde-vragen-api/veelgestelde-vragen-api';
import VCollapsibleSection from '@shared/component/VCollapsibleSection/VCollapsibleSection.vue';
import VMarkdown from '@shared/component/VMarkdown/VMarkdown.vue';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import { computed, ref } from 'vue';
import { type CrmVeelgesteldeVragenGetVeelgesteldeVragenItem as VeelgesteldeVragenItem } from '@services/crm-public/orval-api/model/crmVeelgesteldeVragenGetVeelgesteldeVragenItem';

interface FaqItem {
  id: string;
  vraag: string;
  zoekwoorden: string[];
  antwoord: string;
}

interface FaqCategorie {
  id: string;
  naam?: string;
  vragen: FaqItem[];
}

const zoekterm = ref('');
const openItemId = ref<string | null>(null);

const mapFaqItem = (item: VeelgesteldeVragenItem): FaqItem => ({
  id: item.id,
  vraag: item.vraag,
  zoekwoorden: item.zoekwoorden,
  antwoord: item.antwoordMarkdown,
});

const {
  data: apiFaqCategorieen,
  error,
  isPending,
} = useVeelgesteldeVragenGet({
  query: {
    select: (response) =>
      response.result
        .toSorted((a, b) => a.volgorde - b.volgorde)
        .map((categorie) => ({
          id: categorie.id,
          naam: categorie.naam,
          vragen: categorie.vragen.toSorted((a, b) => a.volgorde - b.volgorde).map(mapFaqItem),
        })),
  },
});

useWatchErrorHandler(error);

const faqCategorieen = computed<FaqCategorie[]>(() => apiFaqCategorieen.value ?? []);

const gefilterdeCategorieen = computed(() => {
  if (zoekterm.value.length < 3) return faqCategorieen.value;

  const term = zoekterm.value.toLowerCase();
  return faqCategorieen.value
    .map((categorie) => ({
      ...categorie,
      vragen: categorie.naam?.toLowerCase().includes(term)
        ? categorie.vragen
        : categorie.vragen.filter(
            (item) =>
              item.vraag.toLowerCase().includes(term) ||
              item.zoekwoorden.some((zoekwoord) => zoekwoord.toLowerCase().includes(term)),
          ),
    }))
    .filter((categorie) => categorie.vragen.length > 0);
});

function isOpen(itemId: string) {
  return openItemId.value === itemId;
}

function toggleItem(itemId: string, open: boolean) {
  openItemId.value = open ? itemId : null;
}
</script>

<template>
  <div>
    <VSearch v-model="zoekterm" placeholder="Zoeken..." class="mb-4 w-full max-w-xs" />

    <div class="rounded-lg bg-white p-4 shadow-sm">
      <VSkeleton v-if="isPending" :count="4" height="md" />

      <div v-else-if="!error && gefilterdeCategorieen.length === 0" class="text-grey-500">
        Geen vragen gevonden voor "{{ zoekterm }}".
      </div>

      <div v-else class="space-y-6">
        <section v-for="categorie in gefilterdeCategorieen" :key="categorie.id">
          <VHeading v-if="categorie.naam" :level="2" appearance="sm" class="mb-2">
            {{ categorie.naam }}
          </VHeading>

          <VCollapsibleSection
            v-for="item in categorie.vragen"
            :key="item.id"
            :open="isOpen(item.id)"
            padding="sm"
            @update:open="(open) => toggleItem(item.id, open)"
          >
            <template #title>
              <span class="font-medium text-primary">{{ item.vraag }}</span>
            </template>
            <template #content>
              <VMarkdown :markdown="item.antwoord" class="text-sm text-grey-700" />
            </template>
          </VCollapsibleSection>
        </section>
      </div>
    </div>
  </div>
</template>
